import { useEffect, useState } from "react";
import { useRouterState } from "@tanstack/react-router";
import { WhatsAppLink } from "./WhatsAppLink";
import { SITE } from "@/lib/site";
import {
  useMessageVariant,
  genericMessage,
  buildTopicMessage,
  withBusinessHoursNote,
  resolveTopicFromPath,
} from "@/lib/whatsapp-message";

export function WhatsAppFab() {
  const [showBox, setShowBox] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const variant = useMessageVariant();
  // El layout no recibe props de página: el título del post sale del loader de /blog/$slug.
  const blogTitle = useRouterState({
    select: (s) =>
      (
        s.matches.find((m) => m.routeId === "/blog/$slug")?.loaderData as
          { post?: { h1?: string } } | undefined
      )?.post?.h1,
  });
  const { topic, intent, topicId } = resolveTopicFromPath(pathname, { blogTitle });
  const displayMessage = withBusinessHoursNote(
    topic ? buildTopicMessage(topic, intent, variant) : genericMessage(variant),
  );

  // Oculto en enlaces públicos operativos (124-portafolio-convocatoria-
  // promotoras): el botón de WhatsApp de la agencia tapa acciones propias del
  // flujo y confunde a quien está revisando/contestando una convocatoria.
  const oculto = pathname.startsWith("/convocatoria") || pathname.startsWith("/portafolio");

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowBox((current) => (dismissed ? current : true));
    }, 9000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (oculto) return null;

  return (
    <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end">
      {showBox && (
        <div className="mb-3 w-72 max-w-[calc(100vw-2.5rem)] rounded-2xl border border-white/10 bg-secondary p-4 text-ink-warm shadow-pop">
          <div className="mb-2 flex items-center gap-3">
            <div className="relative shrink-0">
              <div className="grid h-8 w-8 place-items-center rounded-full bg-whatsapp text-xs font-bold text-white">
                IY
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-secondary bg-whatsapp" />
            </div>
            <div>
              <p className="text-xs font-bold leading-none">{SITE.name}</p>
              <p className="mt-1 text-[10px] leading-none text-ink-warm/50">Activo hoy</p>
            </div>
            <button
              type="button"
              onClick={() => {
                setShowBox(false);
                setDismissed(true);
              }}
              aria-label="Cerrar"
              className="ml-auto p-1 text-ink-warm/50 hover:text-ink-warm"
            >
              ✕
            </button>
          </div>
          <p className="rounded-lg border border-white/5 bg-white/5 p-2.5 text-xs leading-relaxed text-ink-warm/85">
            {displayMessage}
          </p>
          <WhatsAppLink
            topic={topic}
            intent={intent}
            topicId={topicId}
            className="mt-3 block rounded-xl bg-whatsapp py-2 text-center text-xs font-bold text-white hover:opacity-90"
          >
            Iniciar chat
          </WhatsAppLink>
        </div>
      )}

      <WhatsAppLink
        ariaLabel="Hablar por WhatsApp"
        onClick={() => setShowBox(true)}
        topic={topic}
        intent={intent}
        topicId={topicId}
        className="whatsapp-pulse relative inline-flex h-12 w-12 items-center justify-center rounded-full bg-whatsapp text-sm font-semibold text-white shadow-pop transition-transform hover:scale-[1.05]"
      >
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden>
          <path d="M20.52 3.48A11.94 11.94 0 0 0 12.04 0C5.5 0 .2 5.3.2 11.84a11.7 11.7 0 0 0 1.62 5.94L0 24l6.36-1.66a11.86 11.86 0 0 0 5.68 1.44h.01c6.54 0 11.84-5.3 11.84-11.83a11.7 11.7 0 0 0-3.37-8.47ZM12.05 21.3h-.01a9.84 9.84 0 0 1-5.02-1.38l-.36-.21-3.77.98 1-3.67-.23-.38a9.81 9.81 0 0 1-1.5-5.2c0-5.43 4.43-9.85 9.88-9.85a9.83 9.83 0 0 1 9.86 9.86c0 5.42-4.43 9.85-9.85 9.85Zm5.4-7.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.18.2-.35.22-.65.07-.3-.15-1.26-.47-2.4-1.5-.88-.79-1.48-1.76-1.65-2.06-.18-.3-.02-.46.13-.6.13-.13.3-.34.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.6-.92-2.19-.24-.58-.49-.5-.67-.51l-.57-.01a1.1 1.1 0 0 0-.8.37c-.27.3-1.04 1.02-1.04 2.5 0 1.47 1.07 2.89 1.22 3.09.15.2 2.1 3.2 5.1 4.5.71.3 1.27.49 1.7.62.71.22 1.36.19 1.87.12.57-.08 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35Z" />
        </svg>
      </WhatsAppLink>
    </div>
  );
}
